import {
  $, ItemView, log, Model, util
} from '@glu/core';
import RowImageCell from '../cell/imageCell';
import RowButtons from '../cell/rowButtons';
import RowSelectorCell from '../cell/rowSelectorCell';
import SingleRowSelectorCell from '../cell/singleRowSelectorCell';

function getClassName(column, model, val) {
  let className = column.get('className');
  if (util.isFunction(className)) {
    className = className(val, model);
  }
  return className;
}

export default ItemView.extend({
  modelEvents: {
    change: 'renderAndRestoreFocus'
  },

  tagName: 'tr',
  cellViewPrefix: 'cellview',
  template() {
    return '';
  },

  constructor: function (...args) {
    const [options] = args;
    const className = options.rowClassName;
    this.gridCid = options.gridCid;

    if (className) {
      this.className = util.isFunction(className) ?
        className(options.model) :
        className;
    }

    ItemView.prototype.constructor.apply(this, args);
  },

  initialize(options) {
    ItemView.prototype.initialize.call(this, options);
    this.columns = options.columns;
    this.columnViews = [];
  },

  onRender() {
    this.showColumns();
    this.$el.attr('data-model-cid', this.model.cid);
  },

  onBeforeClose() {
    this.closeColumns();
  },

  closeColumns() {
    util.each(this.columnViews, (columnView) => {
      columnView.close();
    });
    this.columnViews = [];
  },

  getValue(column) {
    if (!column.has('field')) {
      return '';
    }

    const baseValue = this.model.get(column.get('field'));

    if (baseValue === null || baseValue === undefined) {
      return column.get('defaultValue') || baseValue;
    }

    let str = baseValue.toString();
    const val = baseValue.valueOf();

    if (!this.model.editRow && util.isFunction(column.get('format'))) {
      str = column.get('format')(val, this.model);
    }

    // Safe mode should be enforced by default.
    if (column.get('safe') !== false) {
      str = util.escape(str);
    }

    return str;
  },

  getTableCell() {
    return $('<td></td>'); // eslint-disable-line backbone/no-native-jquery
  },

  showColumns() {
    this.closeColumns();
    this.columns.each(this.showColumn, this);
  },

  showSimpleCell(column) {
    let val = this.getValue(column);

    if (util.isNull(val) || util.isUndefined(val)) {
      val = '';
    }

    const $td = this.getTableCell();

    $td.append(val.toString());
    const className = getClassName(column, this.model, val);
    if (className) {
      $td.addClass(className);
    }

    let dataTitle;
    if (column.get('safe') === false) {
      try {
        dataTitle = $(val).text(); // eslint-disable-line backbone/no-native-jquery
      } catch (e) {
        dataTitle = val;
      }
    }

    // required syntax for [data-title] selector to work in events
    $td.attr('data-title', (dataTitle || val));

    this.el.appendChild($td[0]);
  },

  showAnchorCell(column) {
    const urlProperty = column.get('url');
    const val = this.getValue(column);
    const url = util.isFunction(urlProperty) ? urlProperty(this.model, column) : urlProperty;
    const $td = this.getTableCell();
    let $a;

    if (url && val) {
      $a = $(`<a href="${url}"/>`); // eslint-disable-line backbone/no-native-jquery

      // use `html()` instead of `text()` because value is already escaped in `getValue()` above.
      $a.append($('<span />').html(val)); // eslint-disable-line backbone/no-native-jquery

      $td.append($a);
    } else {
      $td.text(val || '');
    }

    $td.attr('data-title', val);

    const className = getClassName(column, this.model, val);
    if (className) {
      $td.addClass(className);
    }

    this.el.appendChild($td[0]);
  },

  showRowSelectorCell(column) {
    const val = this.getValue(column);
    const $td = this.getTableCell();

    const inst = new RowSelectorCell({
      el: $td,
      column,
      model: this.model,
      value: val,
      grid: this.options.grid,
      gridCid: this.gridCid
    });

    inst.render();

    const className = getClassName(column, this.model, val);
    if (className) {
      $td.addClass(className);
    }

    $td.append(inst.el);

    this.el.appendChild($td[0]);

    return inst;
  },

  showSingleRowSelectorCell(column) {
    const val = this.getValue(column);
    const $td = this.getTableCell();

    const inst = new SingleRowSelectorCell({
      el: $td,
      column,
      model: this.model,
      value: val
    });

    inst.render();

    const className = getClassName(column, this.model, val);
    if (className) {
      $td.addClass(className);
    }

    $td.append(inst.el);

    this.el.appendChild($td[0]);

    return inst;
  },

  showImageCell(column) {
    let inst;

    if (!column.has('field')) {
      return '';
    }
    const $td = this.getTableCell();

    const imageData = this.model.get(column.get('field'));
    let url;

    // get our value and make sure it's an object
    if (imageData !== undefined && !util.isArray(imageData)) {
      // convert our object data into a model
      const model = new Model(imageData);

      // if we have a format function , use that value for the image src
      const urlProperty = column.get('formatSrc');

      url = util.isFunction(urlProperty) ? urlProperty(imageData, this.model) : undefined;

      // if we have a formatted image path set, use this for an image.
      if (url !== undefined) {
        model.set('src', url);
      }

      // render our image tag in the cell
      inst = new RowImageCell({
        el: $td,
        column,
        model
      });

      inst.render();
      $td.append(inst.el);
    }

    const className = getClassName(column, this.model, url);
    if (className) {
      $td.addClass(className);
    }

    this.el.appendChild($td[0]);

    return inst;
  },

  showRowButtons(column) {
    const view = new RowButtons({
      tagName: 'td',
      className: 'grid-row-buttons',
      model: this.model,
      column,
      gridCid: this.gridCid,
      theme: this.options.theme
    });

    view.render();

    this.el.appendChild(view.el);
    this.$el.width(view.$el.outerWidth());

    return view;
  },

  showCustomCellView(CellView, column, cellViewOptions) {
    const viewOptions = util.extend({
      tagName: 'td',
      model: this.model,
      column,
      value: this.getValue(column)
    }, cellViewOptions);

    const view = new CellView(viewOptions);

    view.render();

    this.listenTo(view, 'all', this.handleCellEvent);

    this.el.appendChild(view.el);

    return view;
  },

  showColumn(column) {
    let columnView;

    if (column.has('condition')) {
      const isFunction = util.isFunction(column.get('condition')) && !column.get('condition')(this.model, column);
      const isBoolean = util.isBoolean(column.get('condition')) && !column.get('condition');
      if (isFunction || isBoolean) {
        return;
      }
    }

    if (column.has('rowButtons')) {
      columnView = this.showRowButtons(column);
    } else if (column.has('cellView')) {
      columnView = this.showCustomCellView(column.get('cellView'), column, column.get('cellViewOptions'));
    } else if (util.isFunction(column.get('type'))) {
      log.warn('Passing a function to column.type in the grid is deprecated. Change "type" to "cellView".');
      columnView = this.showCustomCellView(column.get('type'), column, column.get('cellViewOptions'));
    } else if (column.has('url')) {
      this.showAnchorCell(column);
    } else if (column.get('type') === 'rowSelector') {
      columnView = this.showRowSelectorCell(column);
    } else if (column.get('type') === 'singleRowSelector') {
      columnView = this.showSingleRowSelectorCell(column);
    } else if (column.get('type') === 'image') {
      columnView = this.showImageCell(column);
    } else {
      this.showSimpleCell(column);
    }

    if (columnView) {
      this.columnViews.push(columnView);
    }

    if (!this.options.disableDynamicColumnWidths) {
      const $lastAddedTd = this.$('> td').last();
      const $correspondingTh = this.$el.parents('.grid .js-canvas > table').find('thead th').eq($lastAddedTd.index());
      const data = $correspondingTh.data();
      const columnWidth = data ? data.initialWidth : 'auto';

      $lastAddedTd.css({
        width: columnWidth,
        minWidth: columnWidth,
        maxWidth: columnWidth
      });
    }
  },

  handleCellEvent(name, view, e) {
    this.trigger(name, this, view, e);
  }
});
